import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Building2,
  Calculator,
  CalendarDays,
  Check,
  ChevronRight,
  CreditCard,
  Eye,
  FileText,
  LogOut,
  MapPin,
  Package,
  ReceiptText,
  RotateCcw,
  Save,
  Search,
  UserRound,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { useClientAdminAuth } from "../context/ClientAdminAuthContext";
import { lookupClientInvoices, updateClientInvoice } from "../services/clientAdminApi";

const DISPLAY_FIELDS = [
  ["companyRegisteredName", "Company Registered Name"],
  ["gstin", "GSTIN"],
  ["invoiceNumber", "Invoice Number"],
  ["invoiceDate", "Invoice Date"],
  ["invoiceTime", "Invoice Time"],
  ["saleType", "Sale Type"],
  ["productId", "Product ID"],
  ["productName", "Product Name"],
  ["hsnCode", "HSN Code"],
  ["customerName", "Customer Name"],
  ["customerPhone", "Customer Phone"],
  ["customerEmail", "Customer Email"],
  ["customerAddress", "Customer Address"],
  ["customerCity", "Customer City"],
  ["customerState", "Customer State"],
  ["customerStateCode", "Customer State Code"],
  ["pinCode", "PIN Code"],
  ["placeOfSupply", "Place of Supply"],
  ["paymentMethod", "Payment Method"],
  ["upiReceived", "UPI Received"],
  ["cashReceived", "Cash Received"],
  ["cardReceived", "Card Received"],
  ["otherPaymentReceived", "Other Payment Received"],
  ["itemSubtotal", "Item Subtotal"],
  ["itemDiscount", "Item Discount"],
  ["itemTotal", "Item Total"],
  ["invoiceSubtotal", "Invoice Subtotal"],
  ["invoiceDiscount", "Invoice Discount"],
  ["taxableAmount", "Taxable Amount"],
  ["gstRate", "GST Rate"],
  ["cgst", "CGST"],
  ["sgst", "SGST"],
  ["igst", "IGST"],
  ["totalGst", "Total GST"],
  ["grandTotal", "Grand Total"],
  ["status", "Status"],
  ["reverseCharge", "Reverse Charge"],
  ["phonePeOrderId", "PhonePe Order ID"],
  ["phonePeTransactionId", "PhonePe Transaction ID"],
  ["companyAddress", "Company Address"],
  ["companyPhone", "Company Phone"],
  ["companyEmail", "Company Email"],
  ["companyWebsite", "Company Website"],
  ["cgstRate", "CGST Rate"],
  ["sgstRate", "SGST Rate"],
  ["igstRate", "IGST Rate"],
  ["actualPrice", "Actual Price"],
  ["hsn", "HSN"],
  ["itemAvailability", "Item Availability"],
];

const numericFields = new Set([
  "upiReceived", "cashReceived", "cardReceived", "otherPaymentReceived",
  "itemSubtotal", "itemDiscount", "itemTotal", "invoiceSubtotal", "invoiceDiscount",
  "taxableAmount", "gstRate", "cgst", "sgst", "igst", "totalGst", "grandTotal",
  "cgstRate", "sgstRate", "igstRate", "actualPrice",
]);

const booleanFields = new Set(["reverseCharge", "itemAvailability"]);
const selectFields = {
  saleType: ["ONLINE", "OFFLINE"],
  paymentMethod: ["Cash", "UPI", "Card", "Other", "COD", "ONLINE", "Split"],
  status: ["Pending", "Partially Paid", "Paid"],
  reverseCharge: ["Yes", "No"],
  itemAvailability: ["Yes", "No"],
};

const EDITABLE_FIELDS = DISPLAY_FIELDS.map(([field, label]) => [
  field,
  label,
  field === "invoiceDate" ? "date"
    : field === "customerPhone" || field === "companyPhone" ? "tel"
      : field === "customerEmail" || field === "companyEmail" ? "email"
        : field === "invoiceTime" ? "time"
          : numericFields.has(field) ? "number"
            : selectFields[field] ? "select"
              : "text",
]);

const FIELD_SECTIONS = [
  {
    title: "Company details",
    description: "Seller identity and contact information",
    icon: Building2,
    fields: ["companyRegisteredName", "gstin", "companyAddress", "companyPhone", "companyEmail", "companyWebsite"],
  },
  {
    title: "Invoice details",
    description: "Invoice reference, date and sale channel",
    icon: FileText,
    fields: ["invoiceNumber", "invoiceDate", "invoiceTime", "saleType"],
  },
  {
    title: "Product details",
    description: "Product identity, price and availability",
    icon: Package,
    fields: ["productId", "productName", "hsnCode", "hsn", "actualPrice", "itemAvailability"],
  },
  {
    title: "Customer details",
    description: "Contact and place-of-supply information",
    icon: UserRound,
    fields: ["customerName", "customerPhone", "customerEmail", "customerAddress", "customerCity", "customerState", "customerStateCode", "pinCode", "placeOfSupply"],
  },
  {
    title: "Payment",
    description: "Payment method, received amounts and status",
    icon: CreditCard,
    fields: ["paymentMethod", "upiReceived", "cashReceived", "cardReceived", "otherPaymentReceived", "status", "phonePeOrderId", "phonePeTransactionId"],
  },
  {
    title: "Amounts and tax",
    description: "Invoice totals, GST and reverse-charge settings",
    icon: Calculator,
    fields: ["itemSubtotal", "itemDiscount", "itemTotal", "invoiceSubtotal", "invoiceDiscount", "taxableAmount", "gstRate", "cgst", "sgst", "igst", "totalGst", "grandTotal", "cgstRate", "sgstRate", "igstRate", "reverseCharge"],
  },
];

const editableFieldByName = new Map(EDITABLE_FIELDS.map(([field, label, type]) => [field, { label, type }]));

const moneyFields = new Set([
  "upiReceived", "cashReceived", "cardReceived", "otherPaymentReceived",
  "itemSubtotal", "itemDiscount", "itemTotal", "invoiceSubtotal", "invoiceDiscount",
  "taxableAmount", "cgst", "sgst", "igst", "totalGst", "grandTotal", "actualPrice",
  "cgstRate", "sgstRate", "igstRate", "gstRate",
]);

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (key === "invoiceDate") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (moneyFields.has(key) && typeof value === "number") return value.toFixed(2);
  return String(value);
};

const editableValuesFromInvoice = (invoice) => Object.fromEntries(
  DISPLAY_FIELDS.map(([field]) => {
    const value = invoice[field];
    if (numericFields.has(field)) return [field, String(value ?? 0)];
    if (booleanFields.has(field)) return [field, value ? "Yes" : "No"];
    if (field === "invoiceDate" && value) {
      const date = new Date(value);
      return [field, Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10)];
    }
    return [field, value === null || value === undefined ? "" : String(value)];
  }),
);

export default function ClientAdminDashboard() {
  const navigate = useNavigate();
  const { clientId, logout, isAuthenticated } = useClientAdminAuth();
  const [productId, setProductId] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!isAuthenticated) return <Navigate to="/client-admin/login" replace />;

  const selectedInvoice = selectedIndex === null ? null : invoices[selectedIndex];
  const hasUnsavedChanges = Boolean(selectedInvoice && editForm && EDITABLE_FIELDS.some(([field]) =>
    String(editForm[field] ?? "") !== String(editableValuesFromInvoice(selectedInvoice)[field] ?? "")
  ));
  const signOut = () => {
    logout();
    navigate("/client-admin/login", { replace: true });
  };

  const searchInvoices = async (event) => {
    event.preventDefault();
    const value = productId.trim();
    if (!value) {
      toast.error("Enter a Product ID to search.");
      return;
    }
    setSearching(true);
    setSelectedIndex(null);
    setEditForm(null);
    try {
      const { data } = await lookupClientInvoices(value);
      setInvoices(data.invoices || []);
      toast.success(`Found ${data.invoices?.length || 0} invoice record(s).`);
    } catch (error) {
      setInvoices([]);
      toast.error(error.response?.data?.message || "Unable to find invoices for this Product ID.");
    } finally {
      setSearching(false);
    }
  };

  const selectInvoice = (index) => {
    setSelectedIndex(index);
    setEditForm(editableValuesFromInvoice(invoices[index]));
  };

  const saveInvoice = async (event) => {
    event.preventDefault();
    if (!selectedInvoice || !editForm) return;
    const original = editableValuesFromInvoice(selectedInvoice);
    const updates = {};
    for (const [field] of EDITABLE_FIELDS) {
      if (String(editForm[field] ?? "") === String(original[field] ?? "")) continue;
      if (numericFields.has(field)) updates[field] = Number(editForm[field]);
      else if (booleanFields.has(field)) updates[field] = editForm[field] === "Yes";
      else updates[field] = editForm[field];
    }
    const oldInvoiceNumber = selectedInvoice.invoiceNumber;
    if (Object.hasOwn(updates, "invoiceNumber") && invoices.length > 1) {
      updates.invoiceNumbers = { [oldInvoiceNumber]: updates.invoiceNumber };
      delete updates.invoiceNumber;
    }
    if (!Object.keys(updates).length) {
      toast.info("No invoice fields have changed.");
      return;
    }
    setSaving(true);
    try {
      const lookupProductId = updates.productId || productId.trim();
      const updatedInvoiceNumber = updates.invoiceNumber ||
        updates.invoiceNumbers?.[oldInvoiceNumber] || oldInvoiceNumber;
      await updateClientInvoice(productId.trim(), updates);
      toast.success("Invoice updated.");
      const { data } = await lookupClientInvoices(lookupProductId);
      const refreshedInvoices = data.invoices || [];
      setInvoices(refreshedInvoices);
      const refreshedIndex = refreshedInvoices.findIndex(
        (invoice) => invoice.invoiceNumber === updatedInvoiceNumber,
      );
      if (refreshedIndex >= 0) {
        setSelectedIndex(refreshedIndex);
        setEditForm(editableValuesFromInvoice(refreshedInvoices[refreshedIndex]));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update invoice.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f7f9] text-slate-900">
      <header className="relative overflow-hidden bg-gradient-to-r from-[#350000] via-[#590303] to-[#740a08] text-white shadow-lg">
        <div className="pointer-events-none absolute -right-20 -top-32 h-80 w-80 rounded-full border-[36px] border-white/[0.04]" />
        <div className="relative mx-auto flex max-w-screen-2xl items-center justify-between gap-4 px-4 py-5 sm:px-7 lg:px-10">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-400 text-xl font-black text-[#3b0000] shadow-md">K</div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-amber-300">Kanchira</p>
              <h1 className="text-lg font-bold sm:text-xl">Client Admin</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden rounded-full border border-white/15 bg-white/10 px-3 py-2 text-xs font-medium text-amber-50 sm:inline-flex">
              {clientId}
            </span>
            <button type="button" onClick={signOut} className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-3 py-2 text-sm font-semibold transition hover:bg-white/10">
              <LogOut size={16} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-screen-2xl space-y-7 px-4 py-7 sm:px-7 lg:px-10 lg:py-9">
        <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="grid gap-6 p-5 sm:p-7 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)] lg:items-center lg:px-9 lg:py-8">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-800">
                <ReceiptText size={14} /> Invoice workspace
              </div>
              <h2 className="max-w-xl text-2xl font-black leading-tight tracking-tight text-slate-900 sm:text-3xl">
                Find and manage product invoices
              </h2>
              <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500">
                Search by Product ID to view matching sales and edit invoice details in one place.
              </p>
            </div>
            <form onSubmit={searchInvoices} className="rounded-2xl bg-slate-50 p-2 sm:flex sm:items-center sm:gap-2">
              <label className="sr-only" htmlFor="invoice-product-search">Product ID</label>
              <div className="flex min-h-12 flex-1 items-center gap-3 px-3">
                <Search className="shrink-0 text-slate-400" size={19} />
                <input
                  id="invoice-product-search"
                  className="w-full border-0 bg-transparent text-sm font-medium outline-none placeholder:font-normal placeholder:text-slate-400 focus:ring-0"
                  placeholder="Enter Product ID or MongoDB ID"
                  value={productId}
                  onChange={(event) => setProductId(event.target.value)}
                />
              </div>
              <button type="submit" className="btn-primary inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl px-5 font-semibold sm:w-auto" disabled={searching}>
                <Search size={16} /> {searching ? "Searching..." : "Search"}
              </button>
            </form>
          </div>
        </section>

        {invoices.length > 0 && (
          <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-5 sm:px-7">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-bold text-slate-900">Matching invoices</h2>
                  <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-900">{invoices.length}</span>
                </div>
                <p className="mt-1 text-sm text-slate-500">Results for <span className="font-semibold text-slate-700">{productId.trim()}</span></p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800">
                <Check size={15} /> Product ID based
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="kanchira-table min-w-[760px] !rounded-none !shadow-none">
                <thead>
                  <tr>
                    <th>Invoice</th><th>Date</th><th>Customer</th>
                    <th>Product</th><th>Total</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice, index) => (
                    <tr key={`${invoice.invoiceNumber}-${index}`} className={selectedIndex === index ? "!bg-amber-50" : ""}>
                      <td className="font-semibold">{invoice.invoiceNumber}</td>
                      <td>{formatValue("invoiceDate", invoice.invoiceDate)}</td>
                      <td>{invoice.customerName || "—"}</td>
                      <td>{invoice.productName || invoice.productId}</td>
                      <td className="font-semibold">{formatValue("grandTotal", invoice.grandTotal)}</td>
                      <td>
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${
                          invoice.status === "Paid" ? "bg-emerald-50 text-emerald-700"
                            : invoice.status === "Partially Paid" ? "bg-amber-50 text-amber-800"
                              : "bg-slate-100 text-slate-600"
                        }`}>{invoice.status || "—"}</span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            aria-label={`View all details for invoice ${invoice.invoiceNumber}`}
                            title="View all invoice details"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-900 focus:outline-none focus:ring-4 focus:ring-amber-500/15"
                            onClick={() => setViewingInvoice(invoice)}
                          >
                            <Eye size={17} />
                          </button>
                          <button type="button" className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-amber-200 px-3 py-2 text-xs font-bold text-amber-900 transition hover:border-amber-400 hover:bg-amber-50" onClick={() => selectInvoice(index)}>
                            Edit <ChevronRight size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {viewingInvoice && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-3 backdrop-blur-sm sm:p-6"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setViewingInvoice(null);
            }}
          >
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="invoice-details-title"
              className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              <header className="flex items-start justify-between gap-4 border-b border-slate-100 bg-gradient-to-r from-white to-amber-50/70 px-5 py-5 sm:px-7">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-800">Complete invoice details</p>
                  <h2 id="invoice-details-title" className="mt-1 break-all text-xl font-black text-slate-900 sm:text-2xl">
                    {viewingInvoice.invoiceNumber}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    {viewingInvoice.customerName || "Customer"} <span className="px-1 text-slate-300">·</span> {viewingInvoice.productName || "Product"}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close invoice details"
                  title="Close"
                  onClick={() => setViewingInvoice(null)}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-amber-500/15"
                >
                  <X size={19} />
                </button>
              </header>
              <div className="overflow-y-auto p-4 sm:p-7">
                <div className="mb-5 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                    <Package size={15} className="text-amber-700" /> Product ID: {viewingInvoice.productId || "—"}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                    <CalendarDays size={15} className="text-amber-700" /> {formatValue("invoiceDate", viewingInvoice.invoiceDate)}
                  </span>
                </div>
                <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {DISPLAY_FIELDS.map(([key, label]) => (
                    <div key={key} className="min-w-0 rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-3">
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
                      <dd className="mt-1 break-words text-sm font-semibold leading-5 text-slate-900">
                        {formatValue(key, viewingInvoice[key])}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
              <footer className="flex justify-end border-t border-slate-100 bg-white px-5 py-4 sm:px-7">
                <button
                  type="button"
                  onClick={() => {
                    const index = invoices.indexOf(viewingInvoice);
                    setViewingInvoice(null);
                    if (index >= 0) selectInvoice(index);
                  }}
                  className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-2.5 font-semibold"
                >
                  Edit invoice <ChevronRight size={16} />
                </button>
              </footer>
            </section>
          </div>
        )}

        {selectedInvoice && editForm && (
          <form onSubmit={saveInvoice} className="space-y-5">
            <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-5 bg-gradient-to-br from-white to-amber-50/70 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-7">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#640101] text-white shadow-md shadow-red-950/10">
                    <FileText size={22} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-800">Editing invoice</p>
                    <h2 className="mt-1 break-all text-xl font-black tracking-tight text-slate-900 sm:text-2xl">{selectedInvoice.invoiceNumber}</h2>
                    <p className="mt-1 text-sm text-slate-500">{selectedInvoice.customerName || "Customer"} <span className="px-1.5 text-slate-300">·</span> {selectedInvoice.productName || "Product"}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                    <Package size={15} className="text-amber-700" /> {selectedInvoice.productId}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                    <CalendarDays size={15} className="text-amber-700" /> {formatValue("invoiceDate", selectedInvoice.invoiceDate)}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-3 text-xs leading-5 text-slate-600 sm:flex-row sm:items-center sm:px-7">
                <MapPin size={15} className="shrink-0 text-amber-700" />
                <p>Updates are submitted using Product ID <span className="font-bold text-slate-800">{productId.trim()}</span> and apply to matching invoice records. Payment totals and status must remain consistent.</p>
              </div>
            </section>

            <div className="grid gap-5 xl:grid-cols-2">
              {FIELD_SECTIONS.map(({ title, description, icon: SectionIcon, fields }) => (
                <section key={title} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-800">
                      <SectionIcon size={19} />
                    </span>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">{title}</h3>
                      <p className="mt-0.5 text-xs text-slate-500">{description}</p>
                    </div>
                  </div>
                  <div className="grid gap-x-4 gap-y-4 p-5 sm:grid-cols-2 sm:p-6">
                    {fields.map((field) => {
                      const { label, type } = editableFieldByName.get(field);
                      const isWide = ["customerAddress", "companyAddress", "companyRegisteredName", "productName", "phonePeOrderId", "phonePeTransactionId", "companyWebsite"].includes(field);
                      return (
                        <label key={field} className={`block min-w-0 text-xs font-semibold text-slate-700 ${isWide ? "sm:col-span-2" : ""}`}>
                          <span className="mb-1.5 block">{label}</span>
                          {type === "select" ? (
                            <select
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 outline-none transition focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10"
                              value={editForm[field]}
                              onChange={(event) => setEditForm({ ...editForm, [field]: event.target.value })}
                            >
                              {selectFields[field].map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                          ) : (
                            <input
                              type={type}
                              min={type === "number" ? "0" : undefined}
                              step={type === "number" ? "0.01" : undefined}
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 outline-none transition placeholder:font-normal placeholder:text-slate-400 hover:border-slate-300 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10"
                              value={editForm[field]}
                              onChange={(event) => setEditForm({ ...editForm, [field]: event.target.value })}
                            />
                          )}
                        </label>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>

            <div className="sticky bottom-0 z-10 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.06)] backdrop-blur sm:-mx-7 sm:px-7 lg:-mx-10 lg:px-10">
              <div className="mx-auto flex max-w-screen-2xl flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-center text-xs text-slate-500 sm:text-left">
                  {hasUnsavedChanges ? "You have unsaved changes." : "All changes are up to date."}
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setEditForm(editableValuesFromInvoice(selectedInvoice))}
                    disabled={!hasUnsavedChanges || saving}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
                  >
                    <RotateCcw size={16} /> Discard
                  </button>
                  <button
                    type="submit"
                    disabled={saving || !hasUnsavedChanges}
                    className="btn-primary inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold shadow-md shadow-red-950/10 disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-40 sm:flex-none"
                  >
                    <Save size={16} /> {saving ? "Saving..." : "Save changes"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
